import { FC, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportPaymentsByCustomerCityOrCountryPayload,
  TReportPaymentsByCustomerCityOrCountryData,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import { buildCommonPayloadWithGroupingAndProducts } from '@src/utils/buildCommonPayload';
import { toast } from 'react-toastify';
import {
  exportPaymentsByCustomerCityOrCountryReport,
  getPaymentsByCustomerCityOrCountryReport,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';
import { useCustomerCities } from '@src/hooks/useCustomerCities';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

export const PaymentsByCustomerCity: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<
    TReportPaymentsByCustomerCityOrCountryData[]
  >([]);

  const {
    cityOptions,
    loading: cityOptionsLoading,
    hasMore: cityOptionsHasMore,
    loadMore: loadMoreCityOptions,
    searchCities: searchCityOptions,
  } = useCustomerCities();

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
    enableCities: true,
  });

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } =
    useReportTableColumns<TReportPaymentsByCustomerCityOrCountryData>({
      data,
      selectedItems: [],
      compareToPreviousPeriod: filters.compareToPreviousPeriod,
      compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
      getAccessField: (item) => item.date,
      getMainDataField: (item, key) => item.amount[key] || 0,
      getPreviousPeriodField: (item, key) => item.amount_previous?.[key] || 0,
      getPreviousYearField: (item, key) =>
        item.amount_previous_year?.[key] || 0,
      itemsField: 'amount',
      columnTitleFormatter: (key) => key.toUpperCase(),
      valueFormatter: (_, value) => `$${value.toLocaleString()}`,
      includeTotalsRow: true,
    });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.amount[key] || 0,
    getPreviousPeriodField: (item, key) => item.amount_previous?.[key],
    getPreviousYearField: (item, key) => item.amount_previous_year?.[key],
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const commonPayload = buildCommonPayloadWithGroupingAndProducts(filters);
      const payload: TReportPaymentsByCustomerCityOrCountryPayload = {
        ...commonPayload,
        by_city: true,
        cities: filters.selectedCities,
        by_country: false,
        countries: [],
      };

      const response = await getPaymentsByCustomerCityOrCountryReport(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to generate report.');
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const commonPayload = buildCommonPayloadWithGroupingAndProducts(filters);
      const payload: TReportPaymentsByCustomerCityOrCountryPayload = {
        ...commonPayload,
        by_city: true,
        cities: filters.selectedCities,
        by_country: false,
        countries: [],
      };

      const response =
        await exportPaymentsByCustomerCityOrCountryReport(payload);

      downloadFile(response.data, 'payments-by-customer-city-report');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report.');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Payments By Customer City Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showCitiesFilter
          showGroupByFilter
          showPeriodFilter
          cityOptions={cityOptions}
          cityOptionsHasMore={cityOptionsHasMore}
          cityOptionsLoading={cityOptionsLoading}
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedCities={filters.selectedCities}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCitiesChange={filters.setSelectedCities}
          onCityOptionsLoadMore={loadMoreCityOptions}
          onCitySearchChange={searchCityOptions}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #3D3D3D',
                  borderRadius: '8px',
                  boxShadow: '0px 7px 13px 0px #B6D72C14',
                }}
                formatter={(value: number) => `$${value.toLocaleString()}`}
              />
              {displayItems.map((city, index) => (
                <Line
                  key={city}
                  activeDot={{ r: 6 }}
                  dataKey={city}
                  dot={{ r: 4 }}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}
              {filters.compareToPreviousPeriod &&
                displayItems.map((city, index) => (
                  <Line
                    key={`${city}_previous_period`}
                    dataKey={`${city}_previous_period`}
                    dot={{ r: 3 }}
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((city, index) => (
                  <Line
                    key={`${city}_previous_year`}
                    dataKey={`${city}_previous_year`}
                    dot={{ r: 3 }}
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
